import { useQuery, useReactiveVar } from '@apollo/client';
import { useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import * as yup from 'yup';
import currencyVar from '../apollo/reactive-variables/money';
import dayjs from '../config/dayjs';
import PORTFOLIO_OPTIONS from '../graphql/queries/portfolio-options';
import { formatYearMonthDay, openUrlAndDownload } from '../helpers';
import usePortfolioDownload from './usePortfolioDownload';
import useSelectedCompany from './useSelectedCompany';
import useGetCountryFromUrl from './useGetCountryFromUrl';
import { COUNTRIES } from '../constants/countries';

const PORTFOLIO_FORM_INITIAL_VALUES = {
  portfolioType: '',
  documentFormat: '',
  startDate: dayjs(),
  endDate: dayjs(),
  currency: '',
};

const validationSchema = yup.object({
  documentFormat: yup.string().required('Formato es requerido'),
  portfolioType: yup.string().required('Tipo de documento es requerido'),
});

const usePortfolioForm = (product, customCompany, client = true) => {
  const country = useGetCountryFromUrl();

  const { data, loading } = useQuery(PORTFOLIO_OPTIONS, {
    variables: {
      countryName: country?.name,
      client,
    },
    skip: !country?.name,
  });

  const selectedCompany = useSelectedCompany();
  const companyId = customCompany !== null ? customCompany : selectedCompany?.id;
  const { loading: loadingPortfolio, generatePortfolioUrl } = usePortfolioDownload();

  const currency = useReactiveVar(currencyVar);

  const portfolioFormats = useMemo(() => data?.getPortfolioFormatEnum || [], [data]);
  const portfolioTypes = useMemo(() => data?.getPortfolioTypeEnum || [], [data]);

  const portfolioTypesAvailable = useMemo(() => {
    if (product === 'ORDERING') {
      return portfolioTypes.filter((type) => type.value === 'HISTORIC');
    }
    if (!client && country?.name === COUNTRIES.MEXICO) {
      return portfolioTypes.map((type) => {
        if (type.value === 'DEFAULT_MEXICO') {
          return { ...type, label: 'Cartera morosa vigente (Nueva versión)' };
        }
        return type;
      });
    }
    return portfolioTypes;
  }, [portfolioTypes, product, client, country?.name]);

  const formik = useFormik({
    initialValues: PORTFOLIO_FORM_INITIAL_VALUES,
    validationSchema,
    onSubmit: (values) => {
      generatePortfolioUrl({
        variables: {
          product,
          companyId,
          portfolioType: values.portfolioType || null,
          documentFormat: values.documentFormat,
          startDate: values.startDate
            ? formatYearMonthDay(values.startDate)
            : null,
          endDate: values.endDate ? formatYearMonthDay(values.endDate) : null,
          currency,
        },
        onCompleted: ({ generatePortfolioSummary: { url } }) => {
          openUrlAndDownload(url, 'portfolio');
        },
      });
    },
  });

  const handleRangeChange = useCallback(
    ({ range }) => {
      formik.setFieldValue('startDate', dayjs(range.startDate));
      formik.setFieldValue('endDate', dayjs(range.endDate));
    },
    [formik],
  );

  return {
    formik,
    portfolioFormats,
    portfolioTypesAvailable,
    loading,
    loadingPortfolio,
    handleRangeChange,
  };
};

export default usePortfolioForm;
